import React from "react";
import { Icon, WithTooltip, Tooltip } from "@abb/abb-common-ux-react";
import styled from "styled-components";
import { TruckIcon } from "./TruckIcon";
import { commonUXTheme } from "../../../../styles/commonUXVariables";
import { useTranslation } from "react-i18next";

const IconWrapper = styled.ul`
  align-content: center;
  align-items: center;
  display: flex;
  justify-content: space-around;
  height: 100px;
  width: 100%;
  // margin-right is needed to offset the size of the right most icon container (80px)
  padding-right: 80px;
  margin-top: ${(props) => props.theme.sizes.l};
  margin-bottom: ${(props) => props.theme.sizes.l};
`;

const ListItem = styled.li<{ isActive?: boolean; isLast?: boolean }>`
  flex-grow: ${(props) => (props.isLast ? 0 : 1)};
  height: 0.2em;
  line-height: 1em;
  margin: 0;
  position: relative;
  text-align: right;
  background-color: ${(props) => {
    if (props.isLast) {
      return props.theme.colors.whitePrimary;
    }
    if (props.isActive) {
      return props.theme.colors.blackPrimary;
    }
    return props.theme.colors.grey20;
  }};
  .ABB_CommonUX_Tooltip__trigger {
    display: inline;
  }
`;

export const IconContainer = styled.div<{
  isActive?: boolean;
  isCompleted?: boolean;
  isLast?: boolean;
}>`
  position: absolute;
  top: -40px;
  z-index: 1;
  width: 80px;
  height: 80px;
  border: ${(props) => {
    if (props.isActive && props.isLast) {
      return `0.2em solid ${props.theme.colors.statusGreen}`;
    }
    if (props.isActive) {
      return `0.2em solid ${props.theme.colors.statusYellow}`;
    }
    if (props.isCompleted) {
      return `0.2em solid ${props.theme.colors.blackPrimary}`;
    }
    return `0.2em solid ${props.theme.colors.grey20}`;
  }};
  border-radius: 50%;
  padding: 0.5em;
  background: ${(props) => props.theme.colors.whitePrimary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusIcon = styled(Icon)<{ isActive?: boolean }>`
  i {
    font-size: ${props => props.theme.fonts.sizes.fontSizeXl} !important;
    width: ${props => props.theme.sizes.l} !important;
    height: ${props => props.theme.sizes.l} !important;
  }

  color: ${(props) =>
    props.isActive ? props.theme.colors.blackPrimary : props.theme.colors.grey20};
`;

const ExclamationIcon = styled(Icon)`
  position: absolute;
  left: 45px;
  top: -13px;
`;

interface GraphicalStatusProps {
  statusNumber: number;
}

export const GraphicalStatus = ({ statusNumber }: GraphicalStatusProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <IconWrapper>
      <ListItem isActive={statusNumber > 1}>
        <WithTooltip>
          <IconContainer isActive={statusNumber === 1} isCompleted={statusNumber > 1}>
            <StatusIcon name="abb/document" isActive={statusNumber > 0} />
          </IconContainer>
          <Tooltip>
            <p>{t("Order is received")}</p>
          </Tooltip>
        </WithTooltip>
      </ListItem>

      <ListItem isActive={statusNumber > 2}>
        <WithTooltip>
          <IconContainer isActive={statusNumber === 2} isCompleted={statusNumber > 2}>
            <StatusIcon name="abb/certificate-document-1" isActive={statusNumber > 1} />
          </IconContainer>
          <Tooltip>
            <p>{t("Order is confirmed")}</p>
          </Tooltip>
        </WithTooltip>
      </ListItem>

      <ListItem isActive={statusNumber > 3}>
        <WithTooltip>
          <IconContainer isActive={statusNumber === 3} isCompleted={statusNumber > 3}>
            <StatusIcon name="abb/robot" isActive={statusNumber > 2} />
          </IconContainer>
          <Tooltip>
            <p>{t("Order is in production")}</p>
          </Tooltip>
        </WithTooltip>
      </ListItem>

      <ListItem isActive={statusNumber > 5}>
        <WithTooltip>
          <IconContainer
            isActive={statusNumber === 4 || statusNumber === 5}
            isCompleted={statusNumber > 4}
          >
            <TruckIcon
              fill={
                statusNumber > 3 ? commonUXTheme.colors.blackPrimary : commonUXTheme.colors.grey10
              }
            />
            {statusNumber === 5 ? (
              <ExclamationIcon name="abb/warning-circle-1" sizeClass="large" />
            ) : null}
          </IconContainer>
          <Tooltip>
            {statusNumber === 5 ? (
              <p>{t("Order is partially delivered")}</p>
            ) : (
              <p>{t("Order is shipped")}</p>
            )}
          </Tooltip>
        </WithTooltip>
      </ListItem>

      <ListItem isLast={true}>
        <WithTooltip>
          <IconContainer isActive={statusNumber === 6} isLast={true}>
            <StatusIcon name="abb/check-mark" isActive={statusNumber === 6} />
          </IconContainer>
          <Tooltip>
            <p>{t("Order is delivered")}</p>
          </Tooltip>
        </WithTooltip>
      </ListItem>
    </IconWrapper>
  );
};
